import { isValid, format } from 'date-fns'
import moment from 'moment'
import {
  any,
  array,
  bool,
  func,
  instanceOf,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types'
import { Component, Fragment } from 'react'
import { identity } from '@opallabs/lambda'
import { getTrackableComponent } from '@opallabs/quanta'
import Conditional from '../../../Conditional'
import PopoverV2 from '../../../PopoverV2/Popover'
import { kuiperClassNames, renderIcon } from '../../../utils'
import TextInput from '../../TextInput'
import Month from '../Month'
import { parseDateForMonth } from '../Popover/helpers'

class DatePickerPopoverV2 extends Component {
  static propTypes = {
    allowableDates: oneOfType([object, array]),
    ariaLabel: string,
    classNameObj: object,
    classNames: any,
    date: instanceOf(Date),
    dateFormat: string,
    disabled: bool,
    displayType: oneOf(['text', 'date', 'input']).isRequired,
    focusable: bool,
    icon: string,
    iconOptions: object,
    iconPosition: oneOf(['left', 'right']),
    inputFocus: func,
    onChange: func,
    onClosed: func,
    onKeyDown: func,
    onOpened: func,
    onSelect: func,
    placeholder: string,
    popoverIsClosed: bool,
    popoverOptions: object,
    popoverRef: object,
    preventOverflow: bool,
    propOverrideMode: bool,
    startOfWeek: oneOf([0, 1, 2, 3, 4, 5, 6]),
    tabIndex: number,
    text: oneOfType([object, string]),
    title: string,
    trackers: object,
    triggerOnBlur: func,
  }

  static defaultProps = {
    onChange: () => {},
    onClosed: identity,
    propOverrideMode: false,
    startOfWeek: 0,
    trackers: {},
    triggerOnBlur: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      forceClose: false,
      popoverHandlers: {
        showOnCreate: true,
      },
      popoverIsClosed: true,
    }
  }

  handleKeyPress = (e) => {
    if (![' ', 'Enter'].includes(e.key)) return
    this.popoverContentElement = e.target

    e.target.click()
  }

  get content() {
    const date = this.props.date
      ? parseDateForMonth(moment(this.props.date))
      : undefined

    return (
      <div
        className={kuiperClassNames('calendar-popover-content')}
        ref={this.props.popoverRef}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={this.props.focusable ? 0 : -1}
      >
        {this.props.title && (
          <div className={kuiperClassNames('title')}>{this.props.title}</div>
        )}
        <Month
          allowableDates={this.props.allowableDates}
          onDaySelect={this.selectDateAndClose}
          startOfWeek={this.props.startOfWeek}
          value={date}
          highlighted={date}
        />
      </div>
    )
  }

  textTrigger = () => (
    <span className={kuiperClassNames({ disabled: this.props.disabled })}>
      {this.props.text}
    </span>
  )

  dateTrigger = () => {
    const validDate = isValid(this.props.date)
    const dateText = validDate
      ? format(this.props.date, this.props.dateFormat)
      : this.props.placeholder
    return <span className={kuiperClassNames('date-text')}>{dateText}</span>
  }

  inputTrigger = () => (
    <TextInput
      ariaLabel={this.props.ariaLabel}
      onFocus={this.props.inputFocus}
      onChange={this.props.onChange}
      value={this.props.text}
    />
  )

  selectTrigger = () => {
    let selectedTrigger
    switch (this.props.displayType) {
      case 'text':
        selectedTrigger = this.textTrigger()
        break
      case 'date':
        selectedTrigger = this.dateTrigger()
        break
      case 'input':
        selectedTrigger = this.inputTrigger()
        break
      default:
        break
    }
    return selectedTrigger
  }

  triggerOnFocus = (onInitialClick) =>
    this.props.displayType === 'input' ? onInitialClick : () => {}

  // @TODO This icon is currently marked as `aria-hidden` because the date
  //   picker is a complicated interaction, but the correct thing would be to
  //   mark the `<div>` as a button and add a label to the icon.
  trigger = ({ onInitialClick } = {}) => (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={kuiperClassNames(this.props.classNames, 'datePickerPopover')}
      onClick={onInitialClick || null}
      onFocus={this.triggerOnFocus(onInitialClick)}
      onBlur={this.props.triggerOnBlur}
      onKeyPress={this.handleKeyPress}
      tabIndex={this.props.tabIndex}
    >
      <div
        className={kuiperClassNames('datePickerPopover-innerWrapper')}
        tabIndex={-1}
      >
        <Conditional
          condition={this.props.icon && this.props.iconPosition === 'left'}
        >
          {renderIcon(this.props.icon, {
            height: 14,
            width: 14,
            ariaMode: 'hidden',
            ...this.props.iconOptions,
          })}
        </Conditional>
        {this.selectTrigger()}
        <Conditional
          condition={this.props.icon && this.props.iconPosition === 'right'}
        >
          {renderIcon(this.props.icon, {
            height: 14,
            width: 14,
            ariaMode: 'hidden',
            ...this.props.iconOptions,
          })}
        </Conditional>
      </div>
    </div>
  )

  selectDateAndClose = (date) => {
    this.track('Date Picked', { date: date.toString() })

    this.setState(
      {
        forceClose: true,
      },
      () => {
        this.props.onSelect(date.toDate())
      }
    )
  }

  track = (...args) => {
    if (this.props.trackers.track) {
      this.props.trackers.track(...args)
    }
  }

  renderPopover = () => {
    this.track('Open Popover')
    this.setState({
      popoverIsClosed: false,
    })
  }

  onClosed = () => {
    this.props.onClosed()

    this.track('Close Popover')
    this.setState({
      forceClose: false,
      popoverIsClosed: true,
    })
  }

  renderTriggerOnly = () => {
    if (this.props.propOverrideMode) {
      return this.props.popoverIsClosed
    }
    return this.state.popoverIsClosed
  }

  render() {
    return (
      <Fragment>
        {this.renderTriggerOnly() ? (
          this.trigger({ onInitialClick: this.renderPopover })
        ) : (
          <PopoverV2
            className={kuiperClassNames('date-picker', 'no-padding')}
            classNameObj={this.props.classNameObj}
            forceClose={this.state.forceClose}
            popoverHandlers={this.state.popoverHandlers}
            onClosed={this.onClosed}
            onOpened={this.props.onOpened}
            onKeyDown={this.props.onKeyDown}
            trigger={this.trigger()}
            content={this.content}
            {...this.props.popoverOptions}
          />
        )}
      </Fragment>
    )
  }
}
DatePickerPopoverV2.displayName =
  'Kuiper: DatePickerPopoverV2 ← DatePicker ← Inputs'

export default DatePickerPopoverV2

export const TrackedDatePickerPopoverV2 =
  getTrackableComponent(DatePickerPopoverV2)
